import React, { useContext, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import Spinner from '../components/spinner/spinner';
import Button from '../components/button';
import { Grid, Hidden } from '@mui/material';
import { useIntl } from 'react-intl';
import translations from './registration.i18n';
import { LoginApiContext } from '../api/LoginApiContext';
import { useUpdater } from '../api/useUpdater';
import { useSetDocumentTitle } from '../api/useSetDocumentTitle';
import { TextInput } from '../components/input/textInput';
import * as css from './registration.module.scss';
import SiktLogo from './siktLogo';
import { BottomLinks, DesktopPageTop, SmallPageTop } from './commoncomponents';
import validateEmail from '../util/validateEmail';
import classNames from 'classnames';

interface InputProps {
  onRegister: (email: string) => void;
  posting: boolean;
  desktop?: boolean;
}

const Input: React.FC<React.PropsWithChildren<InputProps>> = ({
  desktop,
  onRegister,
  posting,
}) => {
  const [email, setEmail] = useState('');
  const { formatMessage } = useIntl();

  const labels = useMemo(
    () => ({
      helpText: formatMessage({ id: translations.helptext.id }),
      btnText: formatMessage({ id: translations.button.id }),
      email: formatMessage({ id: translations.email.id }),
    }),
    [formatMessage],
  );

  const isEmail = useMemo(() => validateEmail(email), [email]);

  return (
    <main className={css.contentContainer}>
      <div
        className={classNames(
          css.inputWrapper,
          desktop && css.desktopPageWrapper,
        )}
      >
        <TextInput
          htmlId={'email'}
          label={labels.email}
          value={email}
          onChange={setEmail}
          autoComplete={'off'}
          helpText={labels.helpText}
        />

        <Button.Secondary
          id="confirmButton"
          onClick={() => onRegister(email)}
          disabled={!isEmail || posting}
          className={css.confirmBtn}
        >
          {labels.btnText}
        </Button.Secondary>
        {posting && <Spinner height={'5em'} />}
      </div>
    </main>
  );
};

const RegistrationPage = () => {
  const { inviteId } = useParams<{ inviteId: string }>();
  const { formatMessage } = useIntl();
  const { registreringApi } = useContext(LoginApiContext);
  const { doPost, posting } = useUpdater((redirectUrl: string) => {
    window.location.href = redirectUrl;
  }, true);

  const onRegister = (email: string) => {
    doPost(
      async () =>
        await registreringApi.registreringIdPost({
          pathParams: { id: inviteId || '' },
          body: email,
        }),
    );
  };

  useSetDocumentTitle(formatMessage({ id: translations.title.id }), [
    formatMessage,
  ]);

  return (
    <div className={css.page}>
      <Hidden only={['xs', 'sm']}>
        <div className={css.desktop}>
          <DesktopPageTop
            css={css}
            title={'Arbeidslivsportalen'}
            description={formatMessage({ id: translations.header.id })}
          />
          <Input desktop onRegister={onRegister} posting={posting} />
        </div>
      </Hidden>
      <Hidden only={['md', 'lg', 'xl']}>
        <div className={css.smallPage}>
          <SmallPageTop
            css={css}
            title={'Arbeidslivsportalen'}
            description={formatMessage({ id: translations.header.id })}
          />
          <Input onRegister={onRegister} posting={posting} />
        </div>
      </Hidden>
      <footer>
        <div className={css.space50} />
        <Grid container>
          <Grid item xs={1} />
          <Grid item xs={10}>
            <SiktLogo />
          </Grid>
          <Grid item xs={1} />
        </Grid>
        <BottomLinks css={css} />
      </footer>
    </div>
  );
};

export default RegistrationPage;
